import { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHInputField, FSwitch } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { FORTA_GRAY2, FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { GoalTypes } from './constants';
import TrashIcon from '../../../../svgs/TrashIcon';
import EditOutlinedIcon from '../../../../svgs/EditOutlinedIcon';

import type { ITreatmentPlanGoalTarget } from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface TargetItemProps {
  index: number;
  target: ITreatmentPlanGoalTarget;
  saving: boolean;
  goalType: GoalTypes;
  goalTargets: ITreatmentPlanGoalTarget[];
  goalActive: boolean;
  onSave: (key: string, value: string | number) => void;
  onDelete: () => void;
  onUpdateTargetActivation: (reason: string) => void;
}

function TargetItem(props: TargetItemProps) {
  const {
    index,
    target,
    goalType,
    saving,
    goalTargets,
    goalActive,
    onSave,
    onDelete,
    onUpdateTargetActivation,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [label, setLabel] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [activeStatus, setActiveStatus] = useState<boolean>(true);
  const [updatedActiveStatus, setUpdatedActiveStatus] = useState<boolean>(true);
  const [isReactivating, setIsReactivating] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [instruction, setInstruction] = useState<string>('');

  useEffect(() => {
    if (!target) return;

    setLabel(target.label);
    setActiveStatus(target.is_active);
    setUpdatedActiveStatus(target.is_active);
    if (target.instruction) {
      setInstruction(target.instruction);
    }
  }, [target]);

  useEffect(() => {
    setIsEditable(isTpEditable && goalActive);
  }, [isTpEditable, goalActive]);

  const handleSwitchToggle = (checked: boolean) => {
    const activeTargets = goalTargets.filter((target) => target.is_active);
    if (!checked && goalActive && activeTargets.length === 1) {
      toast.error('Goal should have another active target to deactivate this target.');
      return;
    }

    setActiveStatus(checked);
    setReason('');
    // NOTE: isReactivating for displaying reason input field which was not rendered initially.
    setIsReactivating(true);
  };

  return (
    <Grid container sx={{ borderBottom: `1px solid ${FORTA_GRAY2}`, p: 2 }}>
      <Grid item xs={12}>
        <Typography fontWeight={'bold'} textAlign={'left'} mb={2}>
          {goalType === GoalTypes.BehaviorReduction ? 'Replacement behavior' : 'Target'} {index + 1}
          :
        </Typography>
      </Grid>
      <Grid item xs={9} sx={{ display: 'flex' }}>
        <IconButton disabled={saving || !isEditable} onClick={onDelete}>
          <TrashIcon color={FORTA_BLUE.primary} />
        </IconButton>
        <FHInputField
          sx={{ flex: 1 }}
          inputHeight={40}
          colorTheme={Themes.Primary}
          coloredBackground
          value={label}
          disabled={saving || !isEditable}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
          onBlur={() => {
            onSave('label', label);
            if (instruction?.length < 2) {
              toast.error('Please provide instruction field');
            }
            setError(label.length < 2 ? true : false);
          }}
          error={error}
        />
      </Grid>
      <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          sx={{
            width: 64,
            height: 40,
            border: `2px solid ${FORTA_BLUE.primary}`,
            borderRadius: '20px',
            backgroundColor: editing ? FORTA_BLUE.primary : 'transparent',
            mr: 3,

            '&:hover': {
              backgroundColor: editing ? FORTA_BLUE.primary : 'transparent',
            },
          }}
          onClick={() => setEditing((prev) => !prev)}
        >
          <EditOutlinedIcon color={editing ? FORTA_WHITE : FORTA_BLUE.primary} />
        </IconButton>
        <FSwitch
          data-testid={`targetActiveStatus${activeStatus}`}
          colorTheme={Themes.Primary}
          coloredBackground
          checked={activeStatus}
          disabled={saving || !isEditable || !(target != null && Object.hasOwn(target, 'id'))}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSwitchToggle(e.target.checked)}
        />
      </Grid>
      {/* Temporary UI to unblock QA */}
      <Box sx={{ width: '100%' }} mt={2}>
        <Typography fontWeight={'bold'} textAlign={'left'} mb={2}>
          Instruction:
        </Typography>
        <FHInputField
          sx={{ flex: 1 }}
          fullWidth
          inputHeight={40}
          colorTheme={Themes.Primary}
          coloredBackground
          value={target.instruction ?? ''}
          disabled={true}
          onChange={() => {}}
          onBlur={() => {}}
        />
      </Box>
      {isReactivating && (
        <Grid item xs={9} sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <Typography fontWeight={'bold'} textAlign={'left'} mb={2}>
            Reason for status change:*
          </Typography>
          <FHInputField
            sx={{ flex: 1 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            coloredBackground
            value={reason}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            onBlur={() => {
              if (activeStatus === updatedActiveStatus) return;
              onUpdateTargetActivation(reason);
            }}
            error={reason?.length < 2 ? true : false}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default TargetItem;
